<template>
  <div :class="$style.buttonsContainer">
    <!-- Revert Changes button -->
    <a
      v-if="isFormBeingUpdated"
      :class="$style.revertButton"
      href="javascript: void(0);"
      role="button"
      @click.prevent="showRevertModal"
    >revert settings</a>
    <span
      v-else
      :class="$style.disabledRevertButton"
      role="button"
    >revert settings</span>
    <!-- Update Button  -->
    <FlatActionButton
      :action="handleUpdateButtonClick"
      :buttonClass="[
        'btnCTA',
        'btnDefault',
        {
          'btn-primary': isFormBeingUpdated
        },
      ]"
      :disabled="!(isFormBeingUpdated && areCredentialsValid)"
      :loading="updateButtonLoading"
      text="update"
    />
    <OauthCredentialsWarningModal
      :showOauthModal="showOauthModal"
      @close="handleModalClose"
      @continue="handleIdmConfigUpdateWithOauth"
    />
  </div>
</template>

<script>
import {
  CustomOAuthMessages,
  PENDING_OAUTH_CONFIG_UPDATE,
  scimConnectionTypes,
} from 'client/util/Constants/ApplicationConstants';
import { mapActions, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { notify } from 'client/util';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import IdentityManagementMixin from 'client/applications/mixins/IdentityManagementMixin';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import OauthCredentialsWarningModal from 'client/applications/scim/customConnector/OauthCredentialsWarningModal.vue';

const { success, error } = Constants;
const { application } = ItemTypeConsts;
const ApplicationModel = StoreModelMap[application];
const {
  SCIM,
  CUSTOM,
} = scimConnectionTypes;

export default {
  name: 'GenericScimConnectorUpdateConfigButtons',

  components: {
    FlatActionButton,
    OauthCredentialsWarningModal,
  },

  mixins: [IdentityManagementMixin],

  props: {
    areCredentialsValid: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      updateButtonLoading: false,
      showOauthModal: false,
    };
  },

  computed: {
    ...mapState(ApplicationModel, {
      tokenKey: 'scimTokenKey',
      item: 'item',
    }),

    isFormBeingUpdated() {
      let isConfigChanged = false;
      switch (this.scimConnectionType) {
        case CUSTOM:
          isConfigChanged = this.didCustomApiConfigChanged || this.didOauthConfigChanged;
          break;

        case SCIM:
        default:
          isConfigChanged = this.isGenericConnectorChanged;
          break;
      }
      return this.areCredentialsChanged || isConfigChanged;
    },
  },

  methods: {
    ...mapActions(ApplicationModel, [
      'verifyScimConnection',
      'resetCustomApiConfig',
      'saveAppStateForAuth',
      'setOldCustomApiConfig',
      'updateCustomIdmOauthConfiguration',
    ]),

    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    async handleIdmConfigUpdateWithOauth() {
      if (this.didOauthConfigChanged) {
        try {
          // Save "state" of app model with new changes.
          // Special compound key to guarantee 1 to 1 relationship
          // This to avoid another app with similar conditions to
          // use this session storage save
          this.saveAppStateForAuth(`${PENDING_OAUTH_CONFIG_UPDATE}-${this.item.id}`);

          await this.updateCustomIdmOauthConfiguration();
          if (this.redirectLocation !== '') {
            this.handleRedirectConfigure();
          }
        } catch {
          this.triggerErrorNotification(CustomOAuthMessages.get('AUTH_FAILURE'));
        }
      } else if (this.didCustomApiConfigChanged) {
        try {
          // No OAuth config updates, update custom protocol config ONLY
          await this.updateConfigureScim();
          this.triggerSuccessNotification(CustomOAuthMessages.get('UPDATE_SUCCESS'));
          this.setOldCustomApiConfig();
        } catch {
          this.triggerErrorNotification(CustomOAuthMessages.get('UPDATE_FAILURE'));
        }
      }
    },

    handleModalClose() {
      this.showOauthModal = false;
    },

    handleRevertModalCancel(resolve) {
      ModalEventBus.close();
      resolve(false);
    },

    handleRevertModalContinue(resolve) {
      switch (this.scimConnectionType) {
        case CUSTOM:
          this.resetCustomApiConfig();
          break;
        case SCIM:
        default:
          this.resetGenericData();
      }
      ModalEventBus.close();
      resolve();
    },

    async handleUpdateButtonClick() {
      try {
        this.updateButtonLoading = true;

        await this.updateConnector();

        this.updateButtonLoading = false;
      } catch (e) {
        notify(error, 'There was a problem updating Identity Management.');
        this.updateButtonLoading = false;
      }
    },

    async showRevertModal() {
      return new Promise((resolve) => {
        ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            headerText: 'Reset Content',
            bodyText: 'Any changes you have made to this form will be reset to your previously saved details. Would you like to continue?',
            handleCancel: this.handleRevertModalCancel.bind(this, resolve),
            handleContinue: this.handleRevertModalContinue.bind(this, resolve),
            showClose: true,
          },
        });
      });
    },

    async updateConnector() {
      if (this.isOAuth2 && this.didOauthConfigChanged) {
        this.showOauthModal = true;
      } else {
        await this.verifyScimConnection();
        await this.updateConfigureScim();
        switch (this.scimConnectionType) {
          case CUSTOM:
            this.setOldCustomApiConfig();
            break;

          case SCIM:
          default:
            this.setGenericOldData();
            break;
        }
        this.setTokenKey('');
        notify(success, 'Identity Management integration has been successfully verified.');
      }
    },
  },
};
</script>

<style module>
@import 'css/IdentityManagementCommon.css';

.buttonsContainer button:last-child {
  margin-left: 16px;
}

.disabledRevertButton {
  color: jcGrey400;
  cursor: not-allowed;
  text-decoration: underline;
}

.revertButton,
.revertButton:hover {
  color: jcGrey700;
  text-decoration: underline;
}

</style>
